/* Product discovery: co-purchase evidence, motif match, live best-selling collections. */
(() => {
  if (customElements.get('wl-discovery')) return;
  class WLDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.cfg = JSON.parse(this.querySelector('[data-wl-config]').textContent);
      this.cfg.root = this.cfg.root.replace(/\/?$/, '/');
      this.grid = this.querySelector('[data-wl-grid]');
      this.button = this.querySelector('[data-wl-more]');
      this.status = this.querySelector('[data-wl-status]');
      this.seen = new Set([String(this.cfg.productId), ...this.cfg.cartIds.map(String)]);
      this.excluded = new Set(this.seen);
      this.pool = [];
      this.cache = new Map();
      this.globalNext = null;
      this.globalStarted = false;
      this.finished = false;
      this.busy = false;
      this.button.addEventListener('click', () => this.load());
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) this.load();
      }, {rootMargin: '150px'});
      this.observer.observe(this.querySelector('[data-wl-sentinel]'));
      this.onCart = () => this.refreshCart();
      document.addEventListener('cart:updated', this.onCart);
      document.addEventListener('cart:build', this.onCart);
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.categoryObserver?.disconnect();
      document.removeEventListener('cart:updated', this.onCart);
      document.removeEventListener('cart:build', this.onCart);
    }
    url(path, params = {}) {
      const url = new URL(path, location.origin);
      if (url.origin !== location.origin) throw new Error('Foreign discovery URL');
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      return url;
    }
    async response(url, json = false) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const r = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
        if (!r.ok) throw new Error(`Discovery HTTP ${r.status}`);
        return json ? await r.json() : await r.text();
      } finally { clearTimeout(timeout); }
    }
    async feed(path, params = {}) {
      const url = this.url(path, {section_id: 'wl-discovery-feed', ...params}).href;
      if (!this.cache.has(url)) {
        this.cache.set(url, this.response(url).then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          return {cards: [...doc.querySelectorAll('[data-wl-feed] .wl-discovery-card')], next: doc.querySelector('[data-wl-next]')?.getAttribute('href') || null};
        }).catch(error => { this.cache.delete(url); throw error; }));
      }
      return this.cache.get(url);
    }
    enqueue(cards, source) {
      for (const card of cards) {
        const id = card.dataset.id;
        if (this.seen.has(id)) continue;
        this.seen.add(id);
        card.dataset.recommendationSource = source;
        this.pool.push(card);
      }
    }
    async seed() {
      await this.refreshCart();
      const anchors = this.cfg.productId ? [this.cfg.productId] : this.cartIds.slice(0, 3);
      let ranks = {};
      try {
        const snapshot = await this.response(this.cfg.pairsUrl, true);
        if (Date.now() - Date.parse(snapshot.generatedAt) < 30 * 86400000) ranks = snapshot.pairs || {};
      } catch (_) { /* native recommendations and bestseller fallback remain */ }
      for (const id of anchors) {
        try {
          const related = await this.feed(`${this.cfg.root}recommendations/products`, {product_id: id, limit: 10, intent: 'related'});
          const coPurchase = related.cards.filter(card => new URL(card.querySelector('a').href).searchParams.get('pr_prod_strat') === 'copurchase');
          this.enqueue(coPurchase, 'shopify-copurchase');
        } catch (_) { /* unpublished/new products have no purchase history */ }
        const ranked = (ranks[String(id)] || []).slice(0, 6);
        const results = await Promise.allSettled(ranked.map(handle => this.feed(`${this.cfg.root}products/${encodeURIComponent(handle)}`)));
        results.forEach(r => { if (r.status === 'fulfilled') this.enqueue(r.value.cards, 'paid-order-pairs'); });
      }
      if (this.cfg.familyUrl) {
        try {
          const family = await this.feed(this.cfg.familyUrl, {sort_by: 'best-selling'});
          const garment = /shirt|hoodie|pullover/i.test(this.cfg.productType + this.cfg.productTitle);
          const cards = garment ? [...family.cards].sort((a, b) => Number(/shirt|hoodie|pullover/i.test(b.dataset.productType + b.dataset.title)) - Number(/shirt|hoodie|pullover/i.test(a.dataset.productType + a.dataset.title))) : family.cards;
          this.enqueue(cards, 'motif-match');
        } catch (_) { /* best-selling fallback remains */ }
      }
      this.seeded = true;
    }
    async fill() {
      let pages = 0;
      while (this.pool.length < 6 && pages++ < 4 && (!this.globalStarted || this.globalNext)) {
        const page = await this.feed(this.globalNext || this.cfg.globalUrl, {sort_by: 'best-selling'});
        this.globalStarted = true;
        this.globalNext = page.next;
        this.enqueue(page.cards, 'best-selling');
      }
    }
    async load() {
      if (this.busy || this.finished || !this.isConnected) return;
      this.busy = true;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Weitere Ideen werden geladen …';
      try {
        if (!this.seeded) await this.seed();
        await this.fill();
        let added = 0;
        while (this.pool.length && added < 6 && this.grid.children.length < 20) {
          const card = this.pool.shift();
          if (this.excluded.has(card.dataset.id)) continue;
          this.grid.append(card);
          added++;
        }
        this.status.textContent = '';
        if (this.grid.children.length >= 20 || (!this.pool.length && this.globalStarted && !this.globalNext)) {
          this.finished = true;
          this.button.hidden = true;
          this.observer.disconnect();
          this.categories();
        }
      } catch (_) {
        this.status.textContent = 'Die weiteren Ideen konnten gerade nicht geladen werden. Bitte erneut versuchen.';
      } finally {
        this.busy = false;
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
    categories() {
      const host = this.querySelector('[data-wl-categories]');
      this.categoryObserver = new IntersectionObserver(entries => {
        entries.filter(e => e.isIntersecting).forEach(e => this.category(e.target));
      }, {rootMargin: '150px'});
      for (const group of this.cfg.categories.filter(c => c.url && c.title)) {
        const section = document.createElement('section');
        section.className = 'wl-discovery-category';
        section.group = group;
        const heading = document.createElement('h3');
        heading.append(document.createTextNode(group.title));
        const link = document.createElement('a');
        link.href = this.url(group.url, {sort_by: 'best-selling'}).href;
        link.textContent = 'Alle ansehen';
        heading.append(link);
        const grid = document.createElement('div');
        grid.className = 'wl-discovery-grid';
        const retry = document.createElement('button');
        retry.type = 'button'; retry.className = 'btn'; retry.textContent = 'Kategorie laden';
        retry.addEventListener('click', () => this.category(section));
        section.append(heading, grid, retry);
        host.append(section);
        this.categoryObserver.observe(section);
      }
    }
    async category(section) {
      if (section.loading || section.loaded) return;
      section.loading = true;
      this.categoryObserver.unobserve(section);
      const button = section.querySelector('button');
      button.disabled = true;
      try {
        let next = section.group.url;
        let count = 0;
        const categorySeen = new Set();
        for (let page = 0; page < 4 && next && count < 3; page++) {
          const batch = await this.feed(next, {sort_by: 'best-selling'});
          next = batch.next;
          for (const card of batch.cards) {
            if (categorySeen.has(card.dataset.id)) continue;
            categorySeen.add(card.dataset.id);
            card.dataset.recommendationSource = 'category-best-selling';
            section.querySelector('.wl-discovery-grid').append(card.cloneNode(true));
            if (++count === 3) break;
          }
        }
        section.loaded = true;
        button.remove();
        if (!count) section.hidden = true;
      } catch (_) { button.textContent = 'Erneut laden'; }
      finally { section.loading = false; button.disabled = false; }
    }
    async refreshCart() {
      try {
        const cart = await this.response(this.url(`${this.cfg.root}cart.js`), true);
        this.cartIds = [...new Set(cart.items.map(item => String(item.product_id)))];
        this.excluded = new Set([String(this.cfg.productId), ...this.cartIds]);
        this.excluded.forEach(id => this.seen.add(id));
        this.grid.querySelectorAll('.wl-discovery-card').forEach(card => { if (this.excluded.has(card.dataset.id)) card.remove(); });
      } catch (_) { this.cartIds = this.cfg.cartIds.map(String); }
    }
  }
  customElements.define('wl-discovery', WLDiscovery);
})();
